import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Play } from 'lucide-react';
import { collection, getCountFromServer, query, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import InteractiveVideo from './InteractiveVideo';

/* ═══════════════════════════════════════════════════════════════
   useInView — lightweight IntersectionObserver hook
   ═══════════════════════════════════════════════════════════════ */
function useInView(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setIsVisible(true); obs.unobserve(el); }
    }, { threshold: 0.15, ...options });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, isVisible];
}

/* ═══════════════════════════════════════════════════════════════
   Animated Counter
   ═══════════════════════════════════════════════════════════════ */
function useCounter(target, shouldStart, duration = 2200) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!shouldStart || target === 0) return;
    let start;
    let raf;
    const animate = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 4);
      setCount(Math.floor(ease * target));
      if (p < 1) raf = requestAnimationFrame(animate);
      else setCount(target);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [target, shouldStart]);
  return count;
}

export default function HomeHero() {
  const { currentUser } = useAuth();
  const [heroRef, heroVisible] = useInView();

  const [studentTarget, setStudentTarget] = useState(0);
  const [recentAvatars, setRecentAvatars] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getCountFromServer(collection(db, 'registrations'));
        setStudentTarget(snap.data().count);
      } catch {
        setStudentTarget(120);
      }
    })();
  }, []);
  const studentCount = useCounter(studentTarget, heroVisible);

  const fmt = (n) => {
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return n.toString();
  };

  return (
    <section
      ref={heroRef}
      className="relative overflow-hidden pt-16 sm:pt-20 lg:pt-24 pb-10 lg:pb-10 bg-slate-50 dark:bg-zinc-950 transition-colors duration-500 min-h-[100svh] flex flex-col"
      dir="rtl"
    >
      {/* ── CSS Animations ── */}
      <style>{`
        @keyframes hero-fade-up {
          from { opacity: 0; transform: translateY(40px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes hero-float {
          0%, 100% { transform: translateY(0px); }
          50%      { transform: translateY(-14px); }
        }
        @keyframes hero-glow {
          0%, 100% { opacity: 0.15; }
          50%      { opacity: 0.3; }
        }
        @keyframes hero-red-pulse {
          0%, 100% { transform: translateY(0) scale(1.1); opacity: 0.15; }
          50%      { transform: translateY(-100px) scale(1.3); opacity: 0.25; }
        }
        .hero-red-gradient {
          animation: hero-red-pulse 12s ease-in-out infinite;
        }
        .hero-animate { animation: hero-fade-up 0.9s cubic-bezier(0.16,1,0.3,1) both; }
        .hero-delay-1 { animation-delay: 0.15s; }
        .hero-delay-2 { animation-delay: 0.3s; }
        .hero-delay-3 { animation-delay: 0.45s; }
        .hero-delay-4 { animation-delay: 0.6s; }
      `}</style>

      {/* ── Ambient Background ── */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000006_1px,transparent_1px),linear-gradient(to_bottom,#00000006_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#ffffff04_1px,transparent_1px),linear-gradient(to_bottom,#ffffff04_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_40%,#000_60%,transparent_100%)] pointer-events-none" />

      {/* Top Right Glow */}
      <div className="absolute bottom-0 right-0 -mr-40 sm:-mr-60 -mt-40 sm:-mt-60 w-[350px] sm:w-[500px] lg:w-[700px] h-[350px] sm:h-[500px] lg:h-[700px] bg-primary-500 rounded-full blur-[120px] sm:blur-[160px] pointer-events-none transition-all duration-700 animate-[hero-glow_6s_ease-in-out_infinite]" style={{ opacity: 0.12 }} />
      <div className="absolute bottom-0 left-0 -ml-40 sm:-mr-60 -mt-40 sm:-mt-60 w-[350px] sm:w-[500px] lg:w-[700px] h-[350px] sm:h-[500px] lg:h-[700px] bg-primary-500 rounded-full blur-[120px] sm:blur-[160px] pointer-events-none transition-all duration-700 animate-[hero-glow_6s_ease-in-out_infinite]" style={{ opacity: 0.12 }} />


      {/* New Red Lobab Identity Gradient (expanded) */}
      <div className="absolute -bottom-64 sm:-bottom-96 left-1/2 -translate-x-1/2 w-[140%] h-[600px] sm:h-[900px] bg-primary-600/20 dark:bg-primary-600/30 rounded-full blur-[120px] sm:blur-[200px] pointer-events-none hero-red-gradient" />

      <div className="container mx-auto px-4 relative z-10 flex flex-col items-center text-center my-auto">
        {/* ── Badge ── */}
        {heroVisible && (
          <div className="hero-animate inline-flex items-center gap-2 sm:gap-2.5 py-1.5 sm:py-2 px-3 sm:px-5 rounded-full bg-white/70 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-primary-600 dark:text-primary-400 text-sm sm:text-base lg:text-lg font-bold mb-6 sm:mb-8 backdrop-blur-xl shadow-sm">
            <Sparkles className="w-4.5 h-4.5" />
            <span>+{fmt(studentCount)} طالب مسجل</span>
            <div className="flex -space-x-2 rtl:space-x-reverse mr-2">
              {recentAvatars.length > 0 ? (
                recentAvatars.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt="Student"
                    className="w-7 h-7 rounded-full bg-slate-100 dark:bg-zinc-800 border-2 border-white dark:border-zinc-900 object-cover"
                  />
                ))
              ) : (
                [
                  'https://randomuser.me/api/portraits/men/32.jpg',
                  'https://randomuser.me/api/portraits/women/44.jpg',
                  'https://randomuser.me/api/portraits/men/46.jpg',
                  'https://randomuser.me/api/portraits/women/8.jpg'
                ].map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt="Student"
                    className="w-7 h-7 rounded-full bg-slate-100 dark:bg-zinc-800 border-2 border-white dark:border-zinc-900 object-cover"
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* ── Headline ── */}
        {heroVisible && (
          <h1 className="hero-animate hero-delay-1 text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black leading-[1.2] sm:leading-[1.15] tracking-tight text-slate-900 dark:text-white max-w-4xl mb-6 sm:mb-8">
            افتح{' '}
            <span className="">
              ابواب
            </span>{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-l from-primary-600 via-primary-500 to-primary-700 dark:from-primary-400 dark:via-primary-500 dark:to-primary-600">التصميم</span>
            <br />
            وابنِ مهارة المستقبل
          </h1>
        )}

        {/* ── Sub-headline ── */}
        {heroVisible && (
          <p className="hero-animate hero-delay-2 text-lg sm:text-xl md:text-2xl lg:text-3xl text-slate-500 dark:text-zinc-400 font-bold leading-relaxed max-w-2xl mb-10 sm:mb-14 px-2 sm:px-0">
            <span className="text-primary-600 dark:text-primary-400 font-bold"> مدرسة لُباب</span> منصّة تعليمية متخصصة في الدورات الابداعية وتنمية الشباب وتطويرهم الى سوق العلم.
          </p>
        )}

        {/* ── CTAs ── */}
        {heroVisible && (
          <div className="hero-animate hero-delay-3 flex flex-col sm:flex-row gap-4 sm:gap-5 items-center">
            <Link
              to={currentUser ? '/dashboard' : '/signup'}
              className="group flex items-center justify-center gap-3 bg-primary-600 hover:bg-primary-500 text-white px-7 py-4 sm:px-10 sm:py-5 rounded-2xl font-extrabold text-base sm:text-xl transition-all duration-300 shadow-[0_0_25px_rgba(224,0,0,0.25)] hover:shadow-[0_0_40px_rgba(224,0,0,0.45)] hover:-translate-y-1 ring-1 ring-primary-500/50 w-full sm:w-auto"
            >
              <span>ابدأ رحلتك</span>
              <ArrowLeft className="w-6 h-6 group-hover:-translate-x-2 transition-transform" />
            </Link>
            <Link
              to="/courses"
              className="flex items-center justify-center gap-3 px-7 py-4 sm:px-10 sm:py-5 rounded-2xl font-bold text-base sm:text-xl transition-all duration-300 bg-white/80 dark:bg-white/5 border border-slate-200 dark:border-white/10 hover:border-primary-500/40 text-slate-700 dark:text-zinc-300 backdrop-blur-lg hover:bg-white dark:hover:bg-white/10 shadow-sm w-full sm:w-auto"
            >
              <Play className="w-5 h-5 text-primary-500" />
              <span>تصفح الدورات</span>
            </Link>
          </div>
        )}
      </div>
      <InteractiveVideo />

    </section>

  );
}
